<template>
    <div id="content">
        <div class="admin-page-header">
            <div>
                <h1 class="admin-page-title">{{ $t('users.list.title') }}</h1>
                <p class="admin-page-subtitle" v-if="total">{{ $t('users.list.totalCount', { total }) }}</p>
            </div>
            <div class="header-actions">
                <router-link to="/users/new" class="btn-purple">
                    <i class="bi bi-plus-lg"></i>
                    <span>{{ $t('users.list.add') }}</span>
                </router-link>
            </div>
        </div>
        <div class="admin-card filter-card">
            <div class="admin-section-label">
                <i class="bi bi-funnel"></i>
                {{ $t('users.list.filters.title') }}
            </div>
            <div class="row g-3 align-items-end">
                <div class="col-12 col-md-6 col-lg">
                    <label class="form-label">{{ $t('users.list.filters.search') }}</label>
                    <input type="text" class="form-control search-input" v-model="search" :placeholder="$t('users.list.filters.searchPlaceholder')" @keyup.enter="applyFilters" />
                </div>
                <div class="col-12 col-md-6 col-lg-2">
                    <label class="form-label">{{ $t('users.list.filters.plan') }}</label>
                    <select class="form-select" v-model="planFilter" @change="applyFilters">
                        <option value="">{{ $t('users.list.filters.allPlans') }}</option>
                        <option v-for="plan in plans" :key="plan.id" :value="plan.id">{{ plan.title }}</option>
                    </select>
                </div>
                <div class="col-6 col-md-6 col-lg-2">
                    <label class="form-label">{{ $t('users.list.filters.dateFrom') }}</label>
                    <input type="date" class="form-control" v-model="dateFrom" @change="applyFilters" />
                </div>
                <div class="col-6 col-md-6 col-lg-2">
                    <label class="form-label">{{ $t('users.list.filters.dateTo') }}</label>
                    <input type="date" class="form-control" v-model="dateTo" @change="applyFilters" />
                </div>
                <div class="col-12 col-lg-auto d-flex gap-2 filter-actions">
                    <button class="btn-purple flex-grow-1 justify-content-center" @click="applyFilters">
                        <i class="bi bi-funnel"></i>
                        <span>{{ $t('users.list.filters.apply') }}</span>
                    </button>
                    <div class="dropdown" ref="exportDd">
                        <button
                            class="btn-export dropdown-toggle"
                            type="button"
                            :aria-expanded="exportOpen ? 'true' : 'false'"
                            :disabled="Boolean(exportingFormat)"
                            @click.stop="toggleExport"
                        >
                            <span v-if="exportingFormat" class="export-spinner"></span>
                            <i v-else class="bi bi-download"></i>
                            <span>{{ exportingFormat ? $t('users.list.export.preparing') : $t('users.list.export.button') }}</span>
                        </button>
                        <ul class="dropdown-menu dropdown-menu-end export-menu" :class="{ show: exportOpen }">
                            <li>
                                <button class="dropdown-item export-item" type="button" @click="downloadExport('xlsx')">
                                    <span class="format-icon format-xlsx">XLSX</span>
                                    <span><strong>Excel</strong><small>{{ $t('users.list.export.excelHint') }}</small></span>
                                </button>
                            </li>
                            <li>
                                <button class="dropdown-item export-item" type="button" @click="downloadExport('csv')">
                                    <span class="format-icon format-csv">CSV</span>
                                    <span><strong>CSV</strong><small>{{ $t('users.list.export.csvHint') }}</small></span>
                                </button>
                            </li>
                            <li>
                                <button class="dropdown-item export-item" type="button" @click="downloadExport('pdf')">
                                    <span class="format-icon format-pdf">PDF</span>
                                    <span><strong>PDF</strong><small>{{ $t('users.list.export.pdfHint') }}</small></span>
                                </button>
                            </li>
                            <li><div class="export-note">{{ $t('users.list.export.filtersNote') }}</div></li>
                        </ul>
                    </div>
                    <button v-if="hasActiveFilters" class="btn-ghost" @click="resetFilters" :title="$t('users.list.filters.reset')">
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>
            </div>
            <div v-if="exportError" class="export-error" role="alert">
                <i class="bi bi-exclamation-circle"></i>
                {{ exportError }}
            </div>
            <div v-if="hasActiveFilters" class="active-filters">
                <span v-if="appliedSearch" class="filter-tag" @click="search=''; applyFilters()">
                    <i class="bi bi-search"></i> {{ appliedSearch }} <i class="bi bi-x"></i>
                </span>
                <span v-if="appliedPlan" class="filter-tag" @click="planFilter=''; applyFilters()">
                    <i class="bi bi-piggy-bank"></i> {{ plans.find(p => p.id == appliedPlan)?.title }} <i class="bi bi-x"></i>
                </span>
                <span v-if="appliedDateFrom" class="filter-tag" @click="dateFrom=''; applyFilters()">
                    <i class="bi bi-calendar"></i> {{ $t('users.list.filters.dateFromTag', { date: appliedDateFrom }) }} <i class="bi bi-x"></i>
                </span>
                <span v-if="appliedDateTo" class="filter-tag" @click="dateTo=''; applyFilters()">
                    <i class="bi bi-calendar"></i> {{ $t('users.list.filters.dateToTag', { date: appliedDateTo }) }} <i class="bi bi-x"></i>
                </span>
            </div>
        </div>
        <div v-if="initialLoading" class="text-center py-5"><span class="loading"></span></div>
        <template v-else>
            <div v-if="fetching" class="table-loading-bar"></div>
            <DataTable
                :columns="columns"
                :rows="users"
                :total="total"
                :totalPages="totalPages"
                :currentPage="currentPage"
                :perPage="perPage"
                @row="rowHandler"
                @goToPage="goToPage"
            />
        </template>
    </div>
</template>

<script>
import { opp } from '../../../lib/utils'
import i18n from '../../i18n'
import DataTable from '../DataTable.vue'

export default {
    components: { DataTable },
    data:() => ({
        users: [],
        plans: [],
        total: 0,
        totalPages: 1,
        perPage: opp,
        currentPage: 1,
        search: '',
        planFilter: '',
        dateFrom: '',
        dateTo: '',
        appliedSearch: '',
        appliedPlan: '',
        appliedDateFrom: '',
        appliedDateTo: '',
        initialLoading: true,
        fetching: false,
        exportingFormat: '',
        exportError: '',
        exportOpen: false
    }),
    computed: {
        columns() {
            return [
                { key: 'id', value: this.$t('users.list.columns.id') },
                { key: 'username', value: this.$t('users.list.columns.email') },
                { key: 'phone', value: this.$t('users.list.columns.phone') },
                { key: 'fio', value: this.$t('users.list.columns.fio') },
                { key: 'company_name', value: this.$t('users.list.columns.company') },
                { key: 'registeredAtFormatted', value: this.$t('users.list.columns.registered') },
                { key: 'sourceFormatted', value: this.$t('users.list.columns.source'), badge: true, badgeClass: (row) => 'badge-source badge-source-' + (row.source || 'unknown') },
                { key: 'planInfo', value: this.$t('users.list.columns.plan') },
                { key: 'statusFormatted', value: this.$t('users.list.columns.status'), badge: true, badgeClass: (row) => row.email_verified ? 'badge-success' : 'badge-warning' },
                { key: 'role', value: this.$t('users.list.columns.role') }
            ]
        },
        hasActiveFilters() {
            return this.appliedSearch || this.appliedPlan || this.appliedDateFrom || this.appliedDateTo
        }
    },
    async mounted() {
        this.$emit('setPageTitle', this.$t('users.list.title'))
        document.addEventListener('click', this.closeExportOnOutside)
        this.plans = await this.getPlans()
        await this.applyFilters()
        this.initialLoading = false
    },
    beforeUnmount() {
        document.removeEventListener('click', this.closeExportOnOutside)
    },
    methods: {
        toggleExport() {
            this.exportOpen = !this.exportOpen
        },
        closeExportOnOutside(event) {
            if(this.exportOpen && this.$refs.exportDd && !this.$refs.exportDd.contains(event.target)) {
                this.exportOpen = false
            }
        },
        async getPlans() {
            let response = await this.$api.getPlans()
            if(response.result === 'SUCCESS') {
                return response.data.plans || []
            }
            return []
        },
        rowHandler(obj) {
            this.$router.push('/users/' + obj.item.id)
        },
        async goToPage(page) {
            this.currentPage = page
            this.fetching = true
            await this.fetchData()
            this.fetching = false
        },
        async applyFilters() {
            this.appliedSearch = this.search
            this.appliedPlan = this.planFilter
            this.appliedDateFrom = this.dateFrom
            this.appliedDateTo = this.dateTo
            this.currentPage = 1
            this.fetching = true
            await this.fetchData()
            this.fetching = false
        },
        async resetFilters() {
            this.search = ''
            this.planFilter = ''
            this.dateFrom = ''
            this.dateTo = ''
            await this.applyFilters()
        },
        async fetchData() {
            let response = await this.$api.getUsers(this.currentPage, this.appliedSearch, this.appliedPlan, this.appliedDateFrom, this.appliedDateTo)
            if(response.result === 'SUCCESS') {
                this.users = response.data.users || []
                this.total = response.data.count || 0
                this.totalPages = response.data.totalPages || 1
            } else {
                this.users = []
                this.total = 0
                this.totalPages = 1
            }
        },
        async downloadExport(format) {
            if(this.exportingFormat) return
            this.exportOpen = false
            this.exportingFormat = format
            this.exportError = ''
            try {
                const file = await this.$api.downloadUsers(
                    format,
                    this.appliedSearch,
                    this.appliedPlan,
                    this.appliedDateFrom,
                    this.appliedDateTo,
                    i18n.global.locale.value
                )
                const url = URL.createObjectURL(file.blob)
                const link = document.createElement('a')
                link.href = url
                link.download = file.filename
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.setTimeout(() => URL.revokeObjectURL(url), 1000)
            } catch(error) {
                this.exportError = this.$t('users.list.export.error')
            } finally {
                this.exportingFormat = ''
            }
        }
    }
}
</script>

<style scoped>
.filter-card {
    margin-bottom: 18px;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

.btn-export {
    min-height: 42px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border: 1px solid #d8dee9;
    border-radius: 10px;
    background: #fff;
    color: #334155;
    font-size: 14px;
    font-weight: 600;
    transition: border-color 0.15s ease, background-color 0.15s ease, transform 0.12s ease;
}

.btn-export:hover:not(:disabled) {
    border-color: #a78bfa;
    background: #faf7ff;
    color: #6d28d9;
}

.btn-export:active:not(:disabled) { transform: translateY(1px); }
.btn-export:disabled { cursor: wait; opacity: 0.7; }

/* Export button lives in the filter action column, next to "Найти" */
.filter-actions { flex-wrap: wrap; }
.filter-actions .dropdown { display: flex; }
.filter-actions .btn-export { height: 100%; white-space: nowrap; }
.filter-actions .btn-purple { white-space: nowrap; }

.export-menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    left: auto;
    margin: 0;
    width: 292px;
    max-width: min(292px, calc(100vw - 32px));
    padding: 8px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 18px 45px rgba(15, 23, 42, 0.14);
}

.export-item {
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 9px;
    border-radius: 8px;
    color: #1e293b;
}

.export-item:hover { background: #f8fafc; }
.export-item strong { display: block; font-size: 13px; }
.export-item small { display: block; margin-top: 2px; color: #64748b; font-size: 11px; }

.format-icon {
    width: 42px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    border-radius: 7px;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.04em;
}

.format-xlsx { background: #dcfce7; color: #166534; }
.format-csv { background: #dbeafe; color: #1d4ed8; }
.format-pdf { background: #fee2e2; color: #b91c1c; }

.export-note {
    margin: 7px 4px 2px;
    padding-top: 9px;
    border-top: 1px solid #eef2f7;
    color: #64748b;
    font-size: 11px;
    line-height: 1.45;
}

.export-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid #c4b5fd;
    border-top-color: #6d28d9;
    border-radius: 50%;
    animation: export-spin 0.7s linear infinite;
}

.export-error {
    display: flex;
    align-items: center;
    gap: 7px;
    margin: 0 0 18px;
    padding: 10px 13px;
    border: 1px solid #fecaca;
    border-radius: 9px;
    background: #fef2f2;
    color: #b91c1c;
    font-size: 13px;
}

@keyframes export-spin { to { transform: rotate(360deg); } }

@media (max-width: 767px) {
    .admin-page-header { align-items: flex-start; }
    .header-actions { width: 100%; }
    .header-actions > * { flex: 1; }
    .header-actions .btn-purple { width: 100%; justify-content: center; }
}

.search-input {
    padding-left: 2.25em !important;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: 0.75em center;
    background-size: 14px;
}

.active-filters {
    display: flex;
    gap: 0.5em;
    flex-wrap: wrap;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #e5e7eb;
}

.filter-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background-color: #ede9fe;
    color: #6d28d9;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.filter-tag:hover {
    background-color: #ddd6fe;
}

.filter-tag .bi-x {
    font-size: 14px;
}

.table-loading-bar {
    height: 3px;
    background-image: linear-gradient(135deg, #2B00CE 0%, #BA46A8 50%, #FF9F22 100%);
    border-radius: 2px;
    margin-bottom: -3px;
    animation: loading-bar 1.2s ease-in-out infinite;
}

@keyframes loading-bar {
    0% { opacity: 0.3; transform: scaleX(0.3); transform-origin: left; }
    50% { opacity: 1; transform: scaleX(1); transform-origin: left; }
    100% { opacity: 0.3; transform: scaleX(0.3); transform-origin: right; }
}

/* Table polish (target shared DataTable) */
:deep(.dt-card) {
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
:deep(.table) {
    border-collapse: separate;
    border-spacing: 0;
    margin: 0;
}
:deep(.table thead th) {
    background-color: #faf7ff;
    color: #475569;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 700;
    border-bottom: 1px solid #e5e7eb;
    padding: 12px 16px;
}
:deep(.table tbody td) {
    padding: 12px 16px;
    color: #1e293b;
    border-bottom: 1px solid #f1f5f9;
}
:deep(.table tbody tr:hover) {
    background-color: #faf7ff !important;
}
:deep(.dt-badge.badge-success) {
    background: #dcfce7;
    color: #166534;
    border-radius: 999px;
    padding: 3px 10px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
:deep(.dt-badge.badge-warning) {
    background: #fef3c7;
    color: #92400e;
    border-radius: 999px;
    padding: 3px 10px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
:deep(.dt-badge.badge-source) {
    border-radius: 999px;
    padding: 3px 10px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 600;
}
:deep(.dt-badge.badge-source-register) {
    background: #ede9fe;
    color: #5b21b6;
}
:deep(.dt-badge.badge-source-google) {
    background: #fee2e2;
    color: #b91c1c;
}
:deep(.dt-badge.badge-source-superqr) {
    background: #dbeafe;
    color: #1d4ed8;
}
:deep(.dt-badge.badge-source-unknown) {
    background: #f1f5f9;
    color: #64748b;
}
:deep(.pagination .page-item .page-link) {
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #475569;
    border-radius: 8px;
    margin: 0 2px;
    padding: 6px 12px;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
:deep(.pagination .page-item .page-link:hover) {
    background: #faf7ff;
    border-color: #c4b5fd;
    color: #6d28d9;
}
:deep(.pagination .page-item.active .page-link) {
    background: #7c3aed;
    border-color: #7c3aed;
    color: #fff;
}
</style>
